import { Alert, Empty, Flex, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  useGetKeyenceSampleReportQuery,
  useGetStoredKeyenceSampleReportQuery,
} from '../../../../redux/api/quality';
import type { KeyenceSampleReportFeature } from '../../../../interfaces/Quality.Interface';

const { Text } = Typography;

type FeatureRow = KeyenceSampleReportFeature & {
  column: number;
  /** Measured − nominal, rounded to the CSV's precision. */
  deviation: number | null;
  deviationDecimals: number;
  /** Signed % of the tolerance on the side the measurement went (upper if +, lower if −). */
  toleranceShare: number | null;
};

const decimalsOf = (n: number) => (String(n).split('.')[1] ?? '').length;

const toleranceShareOf = (
  deviation: number,
  upper: KeyenceSampleReportFeature['upper'],
  lower: KeyenceSampleReportFeature['lower'],
): number | null => {
  if (deviation === 0) return 0;
  const side = deviation > 0 ? upper : lower;
  if (typeof side !== 'number' || side === 0) return null;
  const pct = Math.round((Math.abs(deviation) / Math.abs(side)) * 100);
  return deviation > 0 ? pct : -pct;
};

const signedPercent = (value: number) =>
  value > 0 ? `+${value}%` : value < 0 ? `${value}%` : '0%';

const deviationOf = (feature: KeyenceSampleReportFeature) => {
  const { value, design, upper, lower } = feature;
  if (typeof value !== 'number' || typeof design !== 'number') {
    return { deviation: null, deviationDecimals: 0, toleranceShare: null };
  }
  const deviationDecimals = Math.max(decimalsOf(value), decimalsOf(design));
  // Rounding strips float noise such as 0.19006 - 0.188 = 0.0020600000000000063.
  const deviation = Number((value - design).toFixed(deviationDecimals));
  return {
    deviation,
    deviationDecimals,
    toleranceShare: toleranceShareOf(deviation, upper, lower),
  };
};

export type KeyenceReportSource =
  | { kind: 'live'; jobProduct: string; view: string; fileName: string; deviceName?: string }
  | { kind: 'stored'; reportId: string };

export type KeyenceSampleReportResultsProps = {
  source: KeyenceReportSource;
  /** Feature (balloon number) to highlight. */
  activeFeature?: string | null;
  onFeatureClick?: (featureName: string) => void;
};

const display = (value: number | string | null) =>
  value === null || value === '' ? '—' : String(value);

const signed = (value: number | string | null) =>
  typeof value === 'number' && value > 0 ? `+${value}` : display(value);

const errorDetail = (error: unknown): string => {
  if (error && typeof error === 'object' && 'data' in error) {
    const data = (error as { data?: unknown }).data;
    if (data && typeof data === 'object' && 'detail' in data) {
      return String((data as { detail: unknown }).detail);
    }
  }
  return 'Could not load the measurement results from the Keyence device.';
};

const columns: ColumnsType<FeatureRow> = [
  { title: 'Feature', dataIndex: 'name', key: 'name' },
  { title: 'Nominal', dataIndex: 'design', key: 'design', render: display },
  { title: 'Upper Tol', dataIndex: 'upper', key: 'upper', render: signed },
  { title: 'Lower Tol', dataIndex: 'lower', key: 'lower', render: signed },
  {
    title: 'Measured',
    dataIndex: 'value',
    key: 'value',
    render: (value: FeatureRow['value'], row) => (
      <Text strong type={row.in_tolerance === false ? 'danger' : undefined}>
        {display(value)}
      </Text>
    ),
  },
  {
    title: 'Deviation',
    dataIndex: 'deviation',
    key: 'deviation',
    render: (deviation: FeatureRow['deviation'], row) => {
      if (deviation === null) return '—';
      const text = deviation.toFixed(row.deviationDecimals);
      const danger = row.in_tolerance === false ? 'danger' : undefined;
      return (
        <>
          <Text type={danger}>{deviation > 0 ? `+${text}` : text}</Text>
          {row.toleranceShare != null && (
            <Text type={danger ?? 'secondary'} style={{ marginLeft: 6 }}>
              {signedPercent(row.toleranceShare)}
            </Text>
          )}
        </>
      );
    },
  },
  {
    title: 'Result',
    dataIndex: 'in_tolerance',
    key: 'in_tolerance',
    render: (inTolerance: FeatureRow['in_tolerance']) => {
      if (inTolerance === null) return '—';
      return inTolerance ? <Tag color="success">In tolerance</Tag> : <Tag color="error">Out of tolerance</Tag>;
    },
  },
];

/** Per-feature measurements of one Keyence CSV: a linked report from the copy
 *  stored at link time, an unlinked one live off the device via the T150 proxy. */
const KeyenceSampleReportResults = ({ source, activeFeature, onFeatureClick }: KeyenceSampleReportResultsProps) => {
  const live = useGetKeyenceSampleReportQuery(
    source.kind === 'live'
      ? { jobProduct: source.jobProduct, view: source.view, fileName: source.fileName, device_name: source.deviceName }
      : skipToken
  );
  const stored = useGetStoredKeyenceSampleReportQuery(source.kind === 'stored' ? source.reportId : skipToken);
  const { data, isFetching, isError, error } = source.kind === 'stored' ? stored : live;

  if (isError) {
    return <Alert type="warning" showIcon message="Measurement results unavailable" description={errorDetail(error)} />;
  }

  if (!data) {
    return (
      <Flex justify="center" align="center" gap={12} style={{ padding: 24 }}>
        <Spin />
        <Text type="secondary">Loading measurement results</Text>
      </Flex>
    );
  }

  const rows: FeatureRow[] = data.features.map((feature, column) => ({
    ...feature,
    column,
    ...deviationOf(feature),
  }));
  const outOfTolerance = rows.filter((row) => row.in_tolerance === false).length;

  return (
    <Flex vertical gap={8}>
      <Flex gap={12} align="center" wrap="wrap">
        <Text strong>Measurement Results</Text>
        <Tag>{source.kind === 'stored' ? 'Linked report' : 'Live from device'}</Tag>
        {data.judgment && (
          <Tag color={data.judgment === 'OK' ? 'success' : 'error'}>{String(data.judgment)}</Tag>
        )}
        {data.measurement_time && <Text type="secondary">{String(data.measurement_time)}</Text>}
        {outOfTolerance > 0 && <Text type="danger">{outOfTolerance} out of tolerance</Text>}
        <Text type="secondary" style={{ marginLeft: 'auto' }}>
          {data.file_name}
        </Text>
      </Flex>
      <Table<FeatureRow>
        columns={columns}
        dataSource={rows}
        rowKey="column"
        size="small"
        pagination={false}
        loading={isFetching}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: <Empty description="No features in this report" /> }}
        rowClassName={(row) => (activeFeature != null && row.name === activeFeature ? 'ant-table-row-selected' : '')}
        onRow={
          onFeatureClick
            ? (row) => ({ onClick: () => onFeatureClick(row.name), style: { cursor: 'pointer' } })
            : undefined
        }
      />
    </Flex>
  );
};

export default KeyenceSampleReportResults;
